import { useState, useEffect } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Loader2 } from 'lucide-react'
import type { LibraryFolder } from '@/lib/schemas'

interface RenameFolderDialogProps {
  folder: LibraryFolder
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function RenameFolderDialog({
  folder,
  open,
  onOpenChange,
}: RenameFolderDialogProps) {
  const { updateFolder } = useLibraryStore()
  const [name, setName] = useState(folder.name)
  const [isRenaming, setIsRenaming] = useState(false)

  // Reset name when dialog opens
  useEffect(() => {
    if (open) {
      setName(folder.name)
    }
  }, [open, folder.name])

  const handleRename = async () => {
    if (!name.trim() || name === folder.name) return

    setIsRenaming(true)
    try {
      await updateFolder(folder.id, { name: name.trim() })
      onOpenChange(false)
    } catch {
      // Error handled in store
    } finally {
      setIsRenaming(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Rename Folder</DialogTitle>
          <DialogDescription>
            Enter a new name for "{folder.name}".
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="space-y-2">
            <Label htmlFor="folder-name">Folder Name</Label>
            <Input
              id="folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim() && name !== folder.name) {
                  void handleRename()
                }
              }}
              autoFocus
              disabled={isRenaming}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isRenaming}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleRename()}
            disabled={!name.trim() || name === folder.name || isRenaming}
          >
            {isRenaming && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Rename
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
